"use client";

import { useState } from "react";
import { FormControl } from "@mui/material";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  Alert,
} from "@/ui";
import isEmailValid from "@lib/helpers/isEmailValid";
import useCreateCandidate from "@lib/candidates/useCreateCandidate";
import toast from "react-hot-toast";
import TextField from "@components/shared/form/TextField";
import Label from "@components/shared/form/Label";

const INITIAL_CANDIDATE = {
  name: "",
  title: "",
  email: "",
  address: "",
};

export default function AddCandidateManually({ onClose, open, recruiterId, onSwitchToCV }) {
  const [formError, setFormError] = useState("");
  const [candidate, setCandidate] = useState(INITIAL_CANDIDATE);
  const mutation = useCreateCandidate();

  const handleAdd = () => {
    if (!candidate.name || !candidate.email || !candidate.title || !candidate.address) {
      setFormError("Please complete all fields");
      return;
    }

    const data = { ...candidate, recruiter_id: recruiterId };

    mutation.mutate(data, {
      onSuccess: () => {
        toast.success("Candidate added");
        setCandidate(INITIAL_CANDIDATE);
        onClose();
      },
      onError: (error) => {
        let errorMessage = "";
        Object.entries(error.response.data.errors).forEach(([, value]) => {
          errorMessage += value.join("\n");
        });
        setFormError(errorMessage);
      },
    });
  };

  const validateEmail = (email) => {
    if (!email) return;
    setFormError(isEmailValid(email.trim()) ? "" : "Please enter a valid email address");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add candidate</DialogTitle>
            <DialogDescription>
              Enter the candidate details below, or if you have a .pdf or .docx CV,{" "}
              {onSwitchToCV ? (
                <button
                  type="button"
                  onClick={onSwitchToCV}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--action-product)", textDecoration: "underline", font: "inherit", fontSize: "inherit" }}
                >
                  upload CV instead
                </button>
              ) : (
                "upload CV instead"
              )}.
            </DialogDescription>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            {formError && (
              <Alert variant="error" style={{ marginBottom: 16 }}>{formError}</Alert>
            )}
            <FormControl sx={{ width: "100%", mb: 2 }}>
              <Label>Name</Label>
              <TextField
                fullWidth
                id="name"
                variant="outlined"
                autoFocus
                value={candidate.name}
                onChange={(e) => setCandidate((prev) => ({ ...prev, name: e.target.value }))}
              />
            </FormControl>
            <FormControl sx={{ width: "100%", mb: 2 }}>
              <Label>Position title</Label>
              <TextField
                fullWidth
                variant="outlined"
                value={candidate.title}
                onChange={(e) => setCandidate((prev) => ({ ...prev, title: e.target.value }))}
              />
            </FormControl>
            <FormControl sx={{ width: "100%", mb: 2 }}>
              <Label>Email</Label>
              <TextField
                fullWidth
                variant="outlined"
                value={candidate.email}
                onBlur={(e) => validateEmail(e.target.value)}
                onChange={(e) => setCandidate((prev) => ({ ...prev, email: e.target.value }))}
              />
            </FormControl>
            <FormControl sx={{ width: "100%", mb: 2 }}>
              <Label>Location</Label>
              <TextField
                fullWidth
                variant="outlined"
                value={candidate.address}
                onChange={(e) => setCandidate((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="e.g. Sydney, Australia"
              />
            </FormControl>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={handleAdd} disabled={mutation.isPending}>
              {mutation.isPending ? "Adding..." : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
